"use client";

import React, { useCallback, useEffect, useState } from "react";
import { ErrorBoundary as SentryErrorBoundary } from "@sentry/nextjs";
import { Button } from "@/components/ui/button";
import { AlertCircle, Home, RefreshCw, Bug, AlertTriangle, Zap } from "lucide-react";
import { ErrorHandler, RealtimeError, ErrorCode } from "@/lib/utils/error/common";
import { useToast } from "@/hooks/use-toast";

export type ErrorSeverity = 'low' | 'medium' | 'high' | 'critical';

export interface ErrorContext {
  userId?: string;
  route?: string;
  action?: string;
  metadata?: Record<string, any>;
}

interface ErrorFallbackProps {
  error: Error;
  resetError: () => void;
  severity?: ErrorSeverity;
  context?: ErrorContext;
  allowRetry?: boolean;
  maxRetries?: number;
}

function ErrorFallback({ 
  error, 
  resetError, 
  severity = 'medium',
  context,
  allowRetry = true,
  maxRetries = 3
}: ErrorFallbackProps) {
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const { toast } = useToast();

  const getSeverityConfig = useCallback((severity: ErrorSeverity) => {
    switch (severity) {
      case 'low':
        return {
          icon: AlertCircle,
          bgColor: 'bg-blue-100',
          iconColor: 'text-blue-600',
          title: 'Minor Issue Detected',
          message: 'We encountered a small hiccup. This should resolve automatically.'
        };
      case 'medium':
        return {
          icon: AlertTriangle,
          bgColor: 'bg-yellow-100',
          iconColor: 'text-yellow-600',
          title: 'Something Went Wrong',
          message: 'We\'ve encountered an issue. Please try again or return to the home page.'
        };
      case 'high':
        return {
          icon: Bug,
          bgColor: 'bg-orange-100',
          iconColor: 'text-orange-600',
          title: 'System Error',
          message: 'A significant error occurred. Our team has been notified and is working on a fix.'
        };
      case 'critical':
        return {
          icon: Zap,
          bgColor: 'bg-red-100',
          iconColor: 'text-red-600',
          title: 'Critical System Error',
          message: 'A critical error has occurred. Please contact support if this issue persists.'
        };
      default:
        return {
          icon: AlertCircle,
          bgColor: 'bg-gray-100',
          iconColor: 'text-gray-600',
          title: 'Unknown Error',
          message: 'An unexpected error occurred.'
        };
    }
  }, []);

  const handleRetry = useCallback(async () => {
    if (!allowRetry || retryCount >= maxRetries) {
      toast({
        title: "Maximum Retries Exceeded",
        description: "Please refresh the page or contact support.",
        variant: "destructive"
      });
      return;
    }

    setIsRetrying(true);
    setRetryCount(prev => prev + 1);
    
    try {
      // Add delay before retry (exponential backoff)
      const delay = Math.min(1000 * Math.pow(2, retryCount), 5000);
      await new Promise(resolve => setTimeout(resolve, delay));
      
      resetError();
      
      toast({
        title: "Retrying...",
        description: `Attempt ${retryCount + 1} of ${maxRetries}`,
      });
    } catch (retryError) {
      console.error('Retry failed:', retryError);
      toast({
        title: "Retry Failed",
        description: "Please try refreshing the page.",
        variant: "destructive"
      });
    } finally {
      setIsRetrying(false);
    }
  }, [allowRetry, retryCount, maxRetries, resetError, toast]);

  const config = getSeverityConfig(severity);
  const IconComponent = config.icon;

  // Auto-retry for low severity errors
  useEffect(() => {
    if (severity === 'low' && retryCount === 0 && allowRetry) {
      const timer = setTimeout(() => handleRetry(), 2000);
      return () => clearTimeout(timer);
    }
  }, [severity, retryCount, allowRetry, handleRetry]);

  const containerClassName = severity === 'critical' 
    ? "min-h-screen flex items-center justify-center bg-red-50"
    : "min-h-screen flex items-center justify-center bg-gray-50";

  return (
    <div className={containerClassName}>
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
        <div className={`flex items-center justify-center w-12 h-12 mx-auto ${config.bgColor} rounded-full mb-4`}>
          <IconComponent className={`w-6 h-6 ${config.iconColor}`} />
        </div>

        <h1 className="text-xl font-semibold text-gray-900 text-center mb-2">
          {config.title}
        </h1>

        <p className="text-gray-600 text-center mb-6">
          {config.message}
        </p>

        {retryCount > 0 && (
          <div className="mb-4 p-3 bg-blue-50 rounded-lg text-center">
            <p className="text-sm text-blue-700">
              Retry attempt: {retryCount} of {maxRetries}
            </p>
          </div>
        )}

        {process.env.NODE_ENV === "development" && (
          <details className="mb-6 p-4 bg-gray-100 rounded text-sm">
            <summary className="cursor-pointer font-medium">
              Error Details (Dev Only)
            </summary>
            <div className="mt-2">
              <div className="mb-2">
                <strong>Error:</strong> {error.name}: {error.message}
              </div>
              {error.stack && (
                <pre className="whitespace-pre-wrap break-words text-red-600 text-xs">
                  {error.stack}
                </pre>
              )}
              {context && (
                <div className="mt-2">
                  <strong>Context:</strong>
                  <pre className="text-xs mt-1">
                    {JSON.stringify(context, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </details>
        )}

        <div className="flex space-x-3">
          {allowRetry && retryCount < maxRetries && (
            <Button 
              onClick={handleRetry} 
              variant="outline" 
              className="flex-1"
              disabled={isRetrying}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isRetrying ? 'animate-spin' : ''}`} />
              {isRetrying ? 'Retrying...' : 'Try Again'}
            </Button>
          )}

          <Button
            onClick={() => (window.location.href = "/")}
            className="flex-1"
          >
            <Home className="w-4 h-4 mr-2" />
            Go Home
          </Button>
        </div>

        {severity === 'critical' && (
          <div className="mt-4 p-3 bg-red-50 rounded-lg">
            <p className="text-xs text-red-700 text-center">
              If this issue persists, please contact our support team with the error details.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<ErrorFallbackProps>;
  severity?: ErrorSeverity;
  context?: ErrorContext;
  allowRetry?: boolean;
  maxRetries?: number;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

export function ErrorBoundary({ 
  children, 
  fallback,
  severity = 'medium',
  context,
  allowRetry = true,
  maxRetries = 3,
  onError
}: ErrorBoundaryProps) {
  const handleError = useCallback((error: Error, errorInfo: React.ErrorInfo) => {
    // Process error through our error handler
    const errorHandler = ErrorHandler.getInstance();
    const processedError = errorHandler.handleError(error, {
      ...context,
      componentStack: errorInfo.componentStack,
      severity,
    });

    // Call custom error handler if provided
    if (onError) {
      onError(error, errorInfo);
    }

    console.error('Error Boundary caught error:', {
      error: processedError.toJSON(),
      context,
      severity,
    });
  }, [context, severity, onError]);

  const FallbackComponent = fallback || ErrorFallback;

  const SentryBoundary = SentryErrorBoundary as any;

  return (
    <SentryBoundary
      fallback={(props: any) => (
        <FallbackComponent 
          {...props} 
          severity={severity}
          context={context}
          allowRetry={allowRetry}
          maxRetries={maxRetries}
        />
      )}
      beforeCapture={(scope: any, error: any, errorInfo: any) => {
        // Process error first
        handleError(error as Error, errorInfo as any);

        // Add comprehensive context to Sentry
        scope.setContext("errorInfo", {
          componentStack: (errorInfo as any)?.componentStack,
          severity,
        });

        scope.setContext("customContext", (context || {}) as any);

        scope.setTag("errorBoundary", true);
        scope.setTag("severity", severity);

        // Add user context if available
        if (typeof window !== 'undefined') {
          scope.setContext("browser", {
            userAgent: navigator.userAgent,
            url: window.location.href,
            timestamp: new Date().toISOString(),
          });
        }

        // Add route context if available
        if (context?.route) {
          scope.setTag("route", context.route);
        }
      }}
    >
      {children}
    </SentryBoundary>
  );
}

// Specialized error boundaries for different use cases
export function PageErrorBoundary({ children, ...props }: Omit<ErrorBoundaryProps, 'severity'>) {
  return (
    <ErrorBoundary severity="high" {...props}>
      {children}
    </ErrorBoundary>
  );
}

export function ComponentErrorBoundary({ children, ...props }: Omit<ErrorBoundaryProps, 'severity'>) {
  return (
    <ErrorBoundary severity="medium" allowRetry={true} maxRetries={2} {...props}>
      {children}
    </ErrorBoundary>
  );
}

export function CriticalErrorBoundary({ children, ...props }: Omit<ErrorBoundaryProps, 'severity'>) {
  return (
    <ErrorBoundary severity="critical" allowRetry={false} {...props}>
      {children}
    </ErrorBoundary>
  );
}
